/**
 * Tenant Logo Hook
 * - tenant-user: sessionStorage 캐시 → Portal API (portalApiUrl 빈 값 = same-origin)
 */

import { useEffect, useState } from 'react';
import type { User } from '../shared/types';
import { AuthStorage } from '../shared/storage';
import {
  fetchTenantPortalLogo,
  getCachedPortalLogo,
  setCachedPortalLogo,
} from '../shared/tenantPortalLogo';

export interface UseTenantLogoOptions {
  portalApiUrl?: string;
  storage?: AuthStorage;
  storagePrefix?: string;
}

export function useTenantLogo(user: User | null, options: UseTenantLogoOptions = {}) {
  const { portalApiUrl, storage: externalStorage, storagePrefix } = options;
  const tenantId = user?.userType === 'tenant-user' ? user.tenantId : undefined;

  const [tenantLogo, setTenantLogo] = useState<string | undefined>(() => {
    if (!tenantId) return undefined;
    const fromUser =
      user?.userType === 'tenant-user' &&
      'portalLogo' in user &&
      typeof (user as { portalLogo?: string }).portalLogo === 'string'
        ? (user as { portalLogo?: string }).portalLogo?.trim()
        : undefined;
    if (fromUser) return fromUser;
    return getCachedPortalLogo(tenantId);
  });

  useEffect(() => {
    if (user?.userType !== 'tenant-user' || !tenantId) {
      setTenantLogo(undefined);
      return;
    }

    const fromUser =
      'portalLogo' in user && typeof (user as { portalLogo?: string }).portalLogo === 'string'
        ? (user as { portalLogo?: string }).portalLogo?.trim()
        : undefined;
    if (fromUser) {
      setCachedPortalLogo(tenantId, fromUser);
      setTenantLogo(fromUser);
      return;
    }

    const cached = getCachedPortalLogo(tenantId);
    if (cached) setTenantLogo(cached);

    const storage = externalStorage ?? new AuthStorage(storagePrefix);
    const token = storage.getToken();
    if (!token) return;

    let cancelled = false;

    (async () => {
      try {
        const fetched = await fetchTenantPortalLogo(tenantId, token, portalApiUrl);
        if (cancelled) return;
        if (fetched) {
          setCachedPortalLogo(tenantId, fetched);
          setTenantLogo(fetched);
        } else if (!cached) {
          setTenantLogo(undefined);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load tenant logo:', error);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, tenantId, portalApiUrl, externalStorage, storagePrefix]);

  return { tenantLogo };
}
